import { AlertTriangle, Info, WifiOff, X, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/components/ui/cn'
import { t } from '@/lib/i18n'

export type ShellBannerTone = 'info' | 'warning' | 'danger'

const tones: Record<ShellBannerTone, string> = {
  info: 'bg-accent-tint text-text',
  warning: 'bg-warning-tint text-text',
  danger: 'bg-danger-tint text-text',
}
const iconTones: Record<ShellBannerTone, string> = {
  info: 'text-accent-strong',
  warning: 'text-warning',
  danger: 'text-danger',
}
const defaultIcons: Record<ShellBannerTone, LucideIcon> = { info: Info, warning: AlertTriangle, danger: WifiOff }

/** Full width strip (UI.md Banner): icon, text 14, optional action and dismiss. */
export function ShellBanner({
  tone = 'info',
  icon: Icon = defaultIcons[tone],
  children,
  action,
  onDismiss,
  className,
}: {
  tone?: ShellBannerTone
  icon?: LucideIcon
  children: ReactNode
  action?: ReactNode
  onDismiss?: () => void
  className?: string
}) {
  return (
    <div
      role={tone === 'info' ? 'status' : 'alert'}
      className={cn('border-b border-line', tones[tone], className)}
    >
      <div className="mx-auto flex min-h-11 w-full max-w-[1080px] items-center gap-3 px-4 py-2 lg:px-6">
        <Icon size={18} strokeWidth={1.75} className={cn('shrink-0', iconTones[tone])} aria-hidden="true" />
        <div className="min-w-0 flex-1 text-body">{children}</div>
        {action ? <div className="shrink-0">{action}</div> : null}
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('Dismiss')}
            className="focus-ring grid size-8 shrink-0 place-items-center rounded-control text-muted transition-colors duration-150 hover:bg-surface/60 hover:text-text"
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  )
}
